<script lang="ts" setup>
import { ref, watch } from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import { rrulestr } from 'rrule';
import { useDateTime } from '@/composables';
import { useCalendarStore, useEventsModalsStore, useTherapyStore } from '@/store';

const op = ref<OverlayPanel>();
const eventId = ref<number>(0);
const eventTimes = ref<Record<string, Date | string>[]>([]);
const selectedEventDate = ref<Date | null>(null);
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const therapyStore = useTherapyStore();

const toggleOverlayPanel = (toggle: Event | boolean) => op.value?.toggle(toggle);

const onSearchEvent = async () => {
  const calendarStore = useCalendarStore();
  await calendarStore.getEvent(eventId.value.toString());
  eventsModalsStore.eventToDocumentOrEdit = calendarStore.selectedEvent;

  if (!eventsModalsStore.eventToDocumentOrEdit.rrule) {
    selectedEventDate.value = new Date(calendarStore.selectedEvent.start_at);
  } else {
    const rruleObject = rrulestr(eventsModalsStore.eventToDocumentOrEdit.rrule);
    const eventDates = rruleObject.between(new Date(new Date().setHours(-1440)), new Date(new Date().setHours(1440)));

    eventTimes.value = eventDates.map((date) => ({ label: date.toDateString(), value: date }));
  }
};

watch(selectedEventDate, () => {
  if (!selectedEventDate.value) return;

  const startingHours = useDateTime().fromUTCStringToLocalDate(calendarStore.selectedEvent.start_at).getHours();
  const startingDateObject = useDateTime()
    .fromUTCStringToLocalDate(useDateTime().formatDateForApi(selectedEventDate.value, true))
    .setHours(startingHours);
  const endingDateObject = useDateTime().fromUTCStringToLocalDate(calendarStore.selectedEvent.end_at);

  calendarStore.selectedEvent.start_at = new Date(startingDateObject).toString();
  calendarStore.selectedEvent.end_at = new Date(endingDateObject).toString();

  eventsModalsStore.clickedEventClickArgs = {
    id: calendarStore.selectedEvent.id as number,
    start: calendarStore.selectedEvent.start_at,
    startStr: useDateTime().formatDateToLocalISOString(new Date(endingDateObject)),
    end: calendarStore.selectedEvent.end_at,
  };

  eventsModalsStore.eventToEditSelected = true;
  eventsModalsStore.preselectedEventType = eventsModalsStore.eventToDocumentOrEdit?.therapy_event_type;
});

const editEvent = () => {
  toggleOverlayPanel(false);
  eventsModalsStore.toggleEventModal();
};

const unlockEvent = async () => {
  toggleOverlayPanel(false);
  await therapyStore.unlockEvent(calendarStore.selectedEvent.id);
};

const resetValues = () => {
  eventTimes.value = [];
  eventId.value = 0;
  selectedEventDate.value = null;
};
</script>

<template>
  <div class="edit-event-overlay-panel-container mr-3" @click="toggleOverlayPanel">
    <span class="cog-button">
      <Icon name="settings" class="text-color-primary cursor-pointer" />
    </span>
    <OverlayPanel ref="op" @hide="resetValues">
      <div class="p-menu p-component p-menu-overlay p-1">
        <div class="p-menuitem-content">Enter the event id you would like to edit or unlock:</div>
        <InputNumber v-model="eventId" :use-grouping="false"></InputNumber>
        <Dropdown
          v-if="eventTimes.length"
          id="event_times"
          v-model="selectedEventDate"
          option-label="label"
          option-value="value"
          :options="eventTimes"
          placeholder="Select the event date"
        />
        <AppButton
          v-if="!selectedEventDate"
          :text="'Search'"
          type="primary"
          :disabled="!eventId"
          @click="onSearchEvent"
        />
        <div v-if="selectedEventDate" class="inline-flex">
          <AppButton text="Edit Event" type="primary" @click="editEvent" />
          <AppButton
            text="Unlock"
            type="primary"
            :disabled="!calendarStore.selectedEvent?.is_locked"
            @click="unlockEvent"
          />
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<style lang="scss" scoped>
.edit-event-overlay-panel-container {
  width: 30px;
  height: 60px;
  display: flex;
  align-items: center;
}

.cog-button {
  display: inline-flex;
  position: relative;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  transition: 0.2s ease;

  &:hover {
    opacity: 0.75;
  }
}
</style>
